import type { TypedContainerModule } from '@thinkalpha/common/ioc/TypedContainer.js';
import { setSessionServiceForApi, setStoreForApi } from 'src/api';
import { initReactContainer } from 'src/ReactContainer';
// Register client columns (Notes, etc.)
import 'src/features/clientColumns/registration';
// Register slug-based cell renderer overrides (order::side with legs hover card, etc.)
import 'src/features/slugCellRenderers/registration';
import { createStoreCurrentUserQueryCacheUserServiceDataSource } from 'src/services/QueryCacheUserService/storeCurrentUserDataSource';
import { widgetContainerModule } from 'src/services/WorkspaceService/widgetContainerModule';
import { beginBootstrap } from 'src/store/actions/app';
import type { IsomorphicBindings, ReactBindings, StaticBindings } from 'src/types/bindings';

export interface ReactHostModules {
    readonly session?: TypedContainerModule<IsomorphicBindings>;
    readonly additional?: readonly TypedContainerModule<ReactBindings>[];
}

export const createReactContainerAndRunStore = (
    reduxContainerModule: TypedContainerModule<StaticBindings>,
    workspaceServiceModule: TypedContainerModule<IsomorphicBindings>,
    hostModules: ReactHostModules = {},
) => {
    // Create the container
    const container = initReactContainer(hostModules.session);
    for (const hostModule of hostModules.additional ?? []) {
        container.load(hostModule);
    }
    container.load(widgetContainerModule);
    container.load(reduxContainerModule);
    container.load(workspaceServiceModule);

    const store = container.get('Store');
    window.store = store;

    // Some store-related services and values are instantiated here after the store is created
    setStoreForApi(store);
    if (APP_ELECTRON) {
        setSessionServiceForApi(container.get('SessionService'));
    }

    // Instantiate select singletons after store creation
    container.getOptional('History');
    container.get('ShortcutService');
    const currentUserModel = container.get('CurrentUserModel');
    const queryCacheUserServiceDataSource = createStoreCurrentUserQueryCacheUserServiceDataSource(
        store,
        currentUserModel,
    );
    container.bind('QueryCacheUserServiceDataSource').toConstantValue(queryCacheUserServiceDataSource);
    container.get('QueryCacheUserService').initialize(queryCacheUserServiceDataSource);
    container.get('SyncInternalConfigurationsService');
    container.get('SyncToExternalsService');
    container.get('AlertService');

    if (!APP_ELECTRON) {
        store.run();
        const state = store.getState();
        if (!state.app.bootstrapped) {
            store.dispatch(beginBootstrap());
        }
    }

    return container;
};
